import { action, makeObservable, observable, runInAction } from 'mobx';

import { get, getCount, post } from 'stores/_services/dataService';
import { ILocation } from 'stores/location/LocationStore';
import LocationStore from './LocationStore';
import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';

export interface IAutotaskIntegration {
    userName: string;
    password?: string;
    mappings?: Record<string, unknown>;
    queue?: string;
    ticketType?: string;
    severity?: Array<string>;
    sourceType?: Array<string>;
}

export default class AutotaskIntegrationStore extends BaseListStore<LocationStore> {
    integration = {
        userName: ''
    };

    constructor() {
        super({
            itemStore: LocationStore
        });

        makeObservable(this, {
            integration: observable,
            update: action,
            getConfig: action,
            checkConnection: action,
            checkConfig: action,
            setIntegration: action,
            changeEnabled: action,
            setSettings: action,
            getMappings: action,
            setMappings: action,
            getOptions: action
        });
    }

    update(integration: IAutotaskIntegration): void {
        this.integration = {
            ...this.integration,
            ...integration
        };
    }

    public get location() {
        return this.ids.map((id) => {
            const itemStore = this.data.get(id);

            if (itemStore) {
                return {
                    ...itemStore.location,
                    store: itemStore
                };
            }
        });
    }

    async getConfig(): Promise<void> {
        const { data } = await get('psaconfiguration/config');
        return data;
    }

    async checkConnection(): Promise<void> {
        const { data } = await get('psaconfiguration/checkConnection');
        return data;
    }

    async checkConfig(): Promise<void> {
        const { data } = await get('psaconfiguration/checkConfig');
        return data;
    }

    async setIntegration(data: Record<string, unknown>): Promise<void> {
        return await post('psaconfiguration/config', {
            username: data.userName,
            secret: data.password,
            enabled: true
        });
    }

    async changeEnabled(bool: boolean) {
        return await post('psaconfiguration/config', {
            enabled: bool
        });
    }

    async setSettings(data: any, severityMappings: any, alertMappings: any): Promise<Record<string, unknown>> {
        const configRes = await post('psaconfiguration/config', {
            severities: Object.entries(data.severity)
                ?.filter(([key, val]) => val === true)
                .map(([key, val]) => key),
            sourceTypes: Object.entries(data.sourceType)
                ?.filter(([key, val]) => val === true)
                .map(([key, val]) => key),
            issueTypeId: data.issueType,
            queueId: data.queue,
            ticketStatusId: data.ticketStatus,
            noteTypeId: data.noteType,
            notePublishTypeId: data.notePublishType
        });
        const severityMapRes = await post('psaconfiguration/mappings/severities', severityMappings);
        const alertMapRes = await post('psaconfiguration/mappings/alerts', alertMappings);

        return { configRes, severityMapRes, alertMapRes };
    }

    async getMappings(filter: any, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<Record<string, unknown>> {
        const { data } = await get('Locations', {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                order: 'name ASC',
                include: 'organization'
            }
        });
        const totalCount = await getCount('Locations');
        const { data: mappings } = await get('psaconfiguration/mappings/accounts');
        const { data: accounts } = await get('psaconfiguration/accounts');

        const locations = data.map((loc: ILocation) => {
            if (loc.organization) {
                loc.name = `${loc.name} (${loc.organization.name})`;
            }
            return loc;
        });

        this._updateData(locations);
        this._updateIds(locations, 0);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = locations.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });

        return { locations, accounts, mappings };
    }

    async setMappings(data: Record<string, unknown>): Promise<void> {
        return await post('psaconfiguration/mappings/accounts', data);
    }

    async refreshAccount(): Promise<void> {
        const {
            data: { message }
        } = await post('psaconfiguration/refreshAccount');
        return message;
    }

    async getAccountStatus(): Promise<void> {
        return await get('psaconfiguration/accountStatus');
    }

    async unmapAccount(mappingId: string): Promise<void> {
        await post('psaconfiguration/unmap', { mappingId });
    }

    async getOptions(): Promise<Record<string, unknown>> {
        const { data: config } = await get('psaconfiguration/config');
        const { data: severityMappings } = await get('psaconfiguration/mappings/severities');
        const { data: alertMappings } = await get('psaconfiguration/mappings/alerts');
        const { data: severities } = await get('psaconfiguration/severities');
        const { data: queues } = await get('psaconfiguration/queueNames');
        const { data: issueTypes } = await get('psaconfiguration/issueTypes');
        const { data: priorities } = await get('psaconfiguration/priorities');
        const { data: alertFields } = await get('psaconfiguration/alertFields');
        const { data: userDefinedFields } = await get('psaconfiguration/userDefinedFields');
        const { data: ticketStatus } = await get('psaconfiguration/ticketStatus');
        const { data: noteTypes } = await get('psaconfiguration/noteTypes');
        const { data: notePublishTypes } = await get('psaconfiguration/notePublishTypes');

        return {
            config,
            severityMappings,
            severities,
            queues,
            issueTypes,
            priorities,
            alertFields,
            userDefinedFields,
            alertMappings,
            ticketStatus,
            noteTypes,
            notePublishTypes
        };
    }
}
